/* eslint-disable */
import { useEffect, useState } from "react"

import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"

import { ErrorType } from "@/hooks/useValidation"
import useDidMount from "@/hooks/useDidMount"

import { usePermissionStateStore } from "@/store/PermissionState"

import { InboxLimits } from "@/protocols/instance"

import UpsellDialog from "@/components/UpsellDialog"

export type BlockCode = "user-limit-reached" |
	"chatbot-limit-reached" |
	"message-limit-reached" |
	"group-permission-denied" |
	"chatbot-permission-denied" |
	"integration-permission-denied" |
	"message-blast-permission-denied" |
	"inbox-message-group-permission-denied" |
	"inbox-bulk-action-denied" |
	"active-campaign-chat-plugin-denied" |
	"ai-text-enhancement-limit-reached-attendant" |
	"ai-text-enhancement-limit-reached-admin" |
	"automatic-upsell-denied" |
	"trackable-links-denied" |
	"chatbot-block-conversion-events-denied" |
	"trackable-links-temporary-denied" |
	"chatbot-block-conversion-events-temporary-denied" |
	"funnel-denied" |
	"ai-credits-limit-reached" |
	"ai-resource-denied" |
	"agent-permission-denied" |
	"meeting-analyzer-denied" |
	"voice-call-limit-reached" |
	"template-send-limit-reached" |
	"template-send-limit-reached-attendant" |
	"template-send-limit-near-limit" |
	"template-send-limit-near-limit-attendant"

export type LimitValidation = {
	mustBlock: boolean
	blockCode: BlockCode
	extraData: Record<string, unknown>
}

type QuantityLimitsBlockCodes = Record<keyof InboxLimits["quantity"], {
	blockCode: BlockCode
	onErrorShouldBlock: boolean
}>
type PermissionLimitsBlockCodes = Record<keyof InboxLimits["permission"], {
	blockCode: BlockCode
	onErrorShouldBlock: boolean
}>

type SubscriptionLimitsOptions = {
	modalLimitsOptions?: {
		openModalWhenHasNoPermission?: boolean;
		onSaveReturnToLastPage?: boolean;
	}
}

const useSubscriptionLimits = <Type extends keyof InboxLimits>(type: Type, context: keyof InboxLimits[Type], options?: SubscriptionLimitsOptions) => {
	const patternOnErrorShouldBlock = false
	const genericPermissionRoute = "/subscription/permission"

	const [isLoading, setIsLoading] = useState<boolean>(true)

	const { checkPermission } = usePermissionStateStore()
	const userHasPermissionToAccessManagerPanel = checkPermission("manager-panel", "access")

	const [limitValidation, setLimitValidation] = useState<LimitValidation>({
		mustBlock: false,
		blockCode: "" as BlockCode,
		extraData: {}
	})

	const quantityLimitsBlockCodes: QuantityLimitsBlockCodes = {
		user: {
			blockCode: "user-limit-reached",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		chatbot: {
			blockCode: "chatbot-limit-reached",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		active_chatbot: {
			blockCode: "chatbot-limit-reached",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		message: {
			blockCode: "message-limit-reached",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		ai_text_enhancement: {
			blockCode: userHasPermissionToAccessManagerPanel ? "ai-text-enhancement-limit-reached-admin" : "ai-text-enhancement-limit-reached-attendant",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		ai_credits: {
			blockCode: "ai-credits-limit-reached",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		voice_call_minutes: {
			blockCode: "voice-call-limit-reached",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		waba_template_credits: {
			blockCode: userHasPermissionToAccessManagerPanel ? "template-send-limit-reached" : "template-send-limit-reached-attendant",
			onErrorShouldBlock: patternOnErrorShouldBlock
		}
	}

	const permissionLimitsBlockCodes: PermissionLimitsBlockCodes = {
		group: {
			blockCode: "group-permission-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		chatbot: {
			blockCode: "chatbot-permission-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		integration: {
			blockCode: "integration-permission-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		messageBlast: {
			blockCode: "message-blast-permission-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		inbox_message_group: {
			blockCode: "inbox-message-group-permission-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		bulk_contact_action: {
			blockCode: "inbox-bulk-action-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		active_campaign_chat_plugin: {
			blockCode: "active-campaign-chat-plugin-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		can_make_automatic_upsell: {
			blockCode: "automatic-upsell-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		trackable_links: {
			blockCode: "trackable-links-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		chatbot_block_conversion_events: {
			blockCode: "chatbot-block-conversion-events-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		trackable_links_temporary: {
			blockCode: "trackable-links-temporary-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		chatbot_block_conversion_events_temporary: {
			blockCode: "chatbot-block-conversion-events-temporary-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		funnel: {
			blockCode: "funnel-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		agent: {
			blockCode: "agent-permission-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		ai_resources: {
			blockCode: "ai-resource-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		},
		meeting_analyzer: {
			blockCode: "meeting-analyzer-denied",
			onErrorShouldBlock: patternOnErrorShouldBlock
		}
	}

	const blockCodesWithNewBlockScreenEnabled: BlockCode[] = [
		"message-blast-permission-denied",
		"group-permission-denied",
		"inbox-message-group-permission-denied"
	]

	const checkPermissionLimit = async (context: keyof InboxLimits["permission"]): Promise<LimitValidation> => {
		const limitCheckerInfo = permissionLimitsBlockCodes[context as keyof InboxLimits["permission"]]
		try {
			const permission = await ApiService.get(genericPermissionRoute, {
				params: {
					type: "permission",
					context
				}
			})

			return {
				mustBlock: !(permission?.data?.hasPermission),
				blockCode: limitCheckerInfo.blockCode,
				extraData: permission?.data?.extraData
			}
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			return {
				mustBlock: limitCheckerInfo.onErrorShouldBlock,
				blockCode: limitCheckerInfo.blockCode,
				extraData: {}
			}
		}
	}

	const checkQuantityLimit = async (context: keyof InboxLimits["quantity"]): Promise<LimitValidation> => {
		const limitCheckerInfo = quantityLimitsBlockCodes[context as keyof InboxLimits["quantity"]]

		try {
			const permission = await ApiService.get(genericPermissionRoute, {
				params: {
					type: "quantity",
					context
				}
			})

			return {
				mustBlock: !(permission?.data?.hasPermission),
				blockCode: limitCheckerInfo.blockCode,
				extraData: permission?.data?.extraData
			}
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			return {
				mustBlock: limitCheckerInfo.onErrorShouldBlock,
				blockCode: limitCheckerInfo.blockCode,
				extraData: {}
			}
		}
	}
	const setLimit = () => {
		// Semicolon needed due JS' error with declaration followed by auto called function
		let limit = limitValidation;
		(async () => {
			setIsLoading(true)
			if (type === "permission") {
				limit = await checkPermissionLimit(context as keyof InboxLimits["permission"])
			}

			if (type === "quantity") {
				limit = await checkQuantityLimit(context as keyof InboxLimits["quantity"])
			}

			setLimitValidation(limit)
			setIsLoading(false)
		})()
	}

	const handleOpenModal = () => {
		if (limitValidation.mustBlock && options?.modalLimitsOptions?.openModalWhenHasNoPermission) {
			if (blockCodesWithNewBlockScreenEnabled.includes(limitValidation.blockCode)) {
				UpsellDialog.open({
					dialogCode: limitValidation.blockCode,
					onClose: () => {
						if (options?.modalLimitsOptions?.onSaveReturnToLastPage) {
							window.history.back()
						}
					}
				})
			} else {
				UpsellDialog.open({
					dialogCode: limitValidation.blockCode,
					onClose: () => {
						if (options?.modalLimitsOptions?.onSaveReturnToLastPage) {
							window.history.back()
						}
					}
				})
			}
		}
	}

	useEffect(() => {
		handleOpenModal()
		// eslint-disable-next-line
	}, [limitValidation.mustBlock])

	const resetLimit = () => {
		setLimit()
	}

	useDidMount(() => {
		resetLimit()
	})

	return {
		...limitValidation,
		resetLimit,
		isLoading
	}
}

export default useSubscriptionLimits
